import { createAsyncThunk } from '@reduxjs/toolkit';
import { RequestEnum } from 'application/store/reducers/request/model';
import {
  HrDepartmentAdminCreateRequest,
  HrDepartmentAdminUpdateRequest,
  HrDepartmentHrCreateRequest,
  HrDepartmentHrUpdateRequest,
} from '@docbay/schemas';
import { HrsManagementAPI } from 'integration/api/hrManagement';

interface Props {
  page?: number;
  limit?: number;
  languageIso?: string;
  hrUserTypes?: string;
  search?: string;
}

/**
 * Redux thunk for creating a new HR department employee.
 *
 * @param params - Data required to create the HR employee.
 * @returns The created HR employee data, or an error if the request fails.
 */
export const createHr = createAsyncThunk(
  RequestEnum.createHr,
  async (params: HrDepartmentHrCreateRequest, { rejectWithValue }) => {
    try {
      const { data } = await HrsManagementAPI.createHr(params);
      return data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

/**
 * Redux thunk for creating a new HR department admin.
 *
 * @param params - Data required to create the HR admin.
 * @returns The created HR admin data, or an error if the request fails.
 */
export const createAdmin = createAsyncThunk(
  RequestEnum.createAdmin,
  async (params: HrDepartmentAdminCreateRequest, { rejectWithValue }) => {
    try {
      const { data } = await HrsManagementAPI.createAdmin(params);
      return data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

/**
 * Redux thunk for fetching HR department users.
 *
 * @param params - Query parameters to filter the results.
 * @returns The fetched HR users data, or an error if the request fails.
 */
export const fetchHrUsers = createAsyncThunk(
  RequestEnum.fetchHrUsers,
  async (params: Props, { rejectWithValue }) => {
    try {
      const { data } = await HrsManagementAPI.fetchHrUsers(params);
      return data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

/**
 * Redux thunk for updating an existing HR department employee.
 *
 * @param params - Data and the ID of the HR employee to update.
 * @returns The updated HR employee data, or an error if the request fails.
 */
export const editHr = createAsyncThunk(
  RequestEnum.editHr,
  async (
    params: { data: HrDepartmentHrUpdateRequest; hrId: string },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await HrsManagementAPI.editHr(params);
      return data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

/**
 * Redux thunk for updating an existing HR department admin.
 *
 * @param params - Data and the ID of the HR admin to update.
 * @returns The updated HR admin data, or an error if the request fails.
 */
export const editAdmin = createAsyncThunk(
  RequestEnum.editAdmin,
  async (
    params: { data: HrDepartmentAdminUpdateRequest; hrAdminId: string },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await HrsManagementAPI.editAdmin(params);
      return data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

/**
 * Redux thunk for deleting an HR department employee.
 *
 * @param hrId - The ID of the HR employee to delete.
 * @returns The deleted HR employee data, or an error if the request fails.
 */
export const deleteHr = createAsyncThunk(
  RequestEnum.deleteHr,
  async (hrId: string, { rejectWithValue }) => {
    try {
      const { data } = await HrsManagementAPI.deleteHr(hrId);
      return data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

/**
 * Redux thunk for deleting an HR department admin.
 *
 * @param hrAdminId - The ID of the HR admin to delete.
 * @returns The deleted HR admin data, or an error if the request fails.
 */
export const deleteAdmin = createAsyncThunk(
  RequestEnum.deleteAdmin,
  async (hrAdminId: string, { rejectWithValue }) => {
    try {
      const { data } = await HrsManagementAPI.deleteAdmin(hrAdminId);
      return data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);
